import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { pedir } from '../api/cliente';
import { usePermiso } from '../sesion/ProveedorDeSesion';
import { Boton, Insignia } from './ui/basicos';
import { Dialogo } from './ui/dialogo';
import { Campo, Interruptor, Selector } from './ui/formulario';
import { useTextoDeError } from './ui/estados';
import { SeccionDeRecurso } from './ui/seccion-de-recurso';
import type { Columna } from './ui/tabla';

type Sentido = 'ingreso' | 'egreso' | 'ambos';

export interface CuentaParaLaCaja {
  readonly id: string;
  readonly codigo: string;
  readonly nombre: string;
}

export interface ConceptoDeCaja {
  readonly id: string;
  readonly codigo: string | null;
  readonly nombre: string;
  readonly sentido: Sentido;
  readonly cuentaContableId: string;
  /** Nula si la cuenta ya no admite movimientos de caja: el concepto no se puede usar. */
  readonly cuenta: CuentaParaLaCaja | null;
  readonly activo: boolean;
}

const SENTIDOS: readonly Sentido[] = ['egreso', 'ingreso', 'ambos'];

function esSentido(valor: string): valor is Sentido {
  return SENTIDOS.some((sentido) => sentido === valor);
}

/** Las cuentas que la caja puede tocar: las mismas para el concepto y para imputar directo. */
export function useCuentasParaLaCaja(habilitada = true): readonly CuentaParaLaCaja[] {
  const cuentas = useQuery({
    queryKey: ['cuentas-para-la-caja'],
    enabled: habilitada,
    queryFn: () => pedir<CuentaParaLaCaja[]>('/conceptos-de-caja/cuentas'),
  });
  return cuentas.data ?? [];
}

/**
 * Configuración → Conceptos de caja (E37).
 *
 * Por qué entra o sale plata del cajón cuando no es una venta. La cajera elige el concepto; la
 * cuenta la elige acá quien sabe, una vez. Se corrigen y se desactivan, no se borran: los
 * movimientos que los usaron los siguen nombrando.
 */
export function ConceptosDeCaja(): React.ReactElement {
  const { t } = useTranslation();
  const puedeAdministrar = usePermiso('CAJA.CONCEPTO.ADMINISTRAR');
  const cuentas = useCuentasParaLaCaja();

  const [nombre, setNombre] = useState('');
  const [sentido, setSentido] = useState<Sentido>('egreso');
  const [cuentaId, setCuentaId] = useState('');
  const [corrigiendo, setCorrigiendo] = useState<ConceptoDeCaja | null>(null);

  const columnas: Columna<ConceptoDeCaja>[] = [
    { clave: 'nombre', titulo: t('conceptosDeCaja.nombre'), contenido: (fila) => fila.nombre },
    {
      clave: 'sentido',
      titulo: t('conceptosDeCaja.sentido'),
      contenido: (fila) => t(`conceptosDeCaja.sentidos.${fila.sentido}`),
    },
    {
      clave: 'cuenta',
      titulo: t('conceptosDeCaja.cuenta'),
      contenido: (fila) =>
        fila.cuenta === null ? (
          <span role="status" className="text-xs text-atencion">
            {t('conceptosDeCaja.cuentaNoDisponible')}
          </span>
        ) : (
          <>
            <span className="numero">{fila.cuenta.codigo}</span> {fila.cuenta.nombre}
          </>
        ),
    },
    {
      clave: 'estado',
      titulo: t('conceptosDeCaja.estado'),
      contenido: (fila) => (
        <Insignia tono={fila.activo ? 'bien' : 'neutro'}>
          {fila.activo ? t('conceptosDeCaja.activo') : t('conceptosDeCaja.inactivo')}
        </Insignia>
      ),
    },
    {
      clave: 'acciones',
      titulo: t('cuentas.acciones'),
      contenido: (fila) =>
        puedeAdministrar ? (
          <Boton
            onClick={() => {
              setCorrigiendo(fila);
            }}
          >
            {t('conceptosDeCaja.corregir')}
          </Boton>
        ) : null,
    },
  ];

  return (
    <>
      <SeccionDeRecurso<ConceptoDeCaja>
        titulo={t('conceptosDeCaja.titulo')}
        descripcion={t('conceptosDeCaja.descripcion')}
        clave={['conceptos-de-caja']}
        ruta="/conceptos-de-caja"
        columnas={columnas}
        claveDeFila={(fila) => fila.id}
        textoVacio={t('conceptosDeCaja.vacio')}
        puedeCrear={puedeAdministrar}
        tituloDelAlta={t('conceptosDeCaja.nuevo')}
        formulario={
          <CamposDelConcepto
            cuentas={cuentas}
            nombre={nombre}
            sentido={sentido}
            cuentaId={cuentaId}
            onNombre={setNombre}
            onSentido={setSentido}
            onCuenta={setCuentaId}
          />
        }
        cuerpoDelAlta={() => ({ nombre: nombre.trim(), sentido, cuentaContableId: cuentaId })}
        alGuardar={() => {
          setNombre('');
          setSentido('egreso');
          setCuentaId('');
        }}
      />

      {corrigiendo === null ? null : (
        <CorregirConcepto
          key={corrigiendo.id}
          concepto={corrigiendo}
          cuentas={cuentas}
          onCerrar={() => {
            setCorrigiendo(null);
          }}
        />
      )}
    </>
  );
}

function CamposDelConcepto({
  cuentas,
  nombre,
  sentido,
  cuentaId,
  onNombre,
  onSentido,
  onCuenta,
}: {
  readonly cuentas: readonly CuentaParaLaCaja[];
  readonly nombre: string;
  readonly sentido: Sentido;
  readonly cuentaId: string;
  readonly onNombre: (valor: string) => void;
  readonly onSentido: (valor: Sentido) => void;
  readonly onCuenta: (valor: string) => void;
}): React.ReactElement {
  const { t } = useTranslation();
  return (
    <>
      <Campo
        etiqueta={t('conceptosDeCaja.nombre')}
        obligatorio
        maxLength={80}
        value={nombre}
        onChange={(evento) => {
          onNombre(evento.target.value);
        }}
      />
      <Selector
        etiqueta={t('conceptosDeCaja.sentido')}
        value={sentido}
        onChange={(evento) => {
          if (esSentido(evento.target.value)) onSentido(evento.target.value);
        }}
      >
        {SENTIDOS.map((valor) => (
          <option key={valor} value={valor}>
            {t(`conceptosDeCaja.sentidos.${valor}`)}
          </option>
        ))}
      </Selector>
      <Selector
        etiqueta={t('conceptosDeCaja.cuenta')}
        obligatorio
        ayuda={t('caja.ayudaCuenta')}
        value={cuentaId}
        onChange={(evento) => {
          onCuenta(evento.target.value);
        }}
      >
        <option value="">{t('caja.elegirCuenta')}</option>
        {cuentas.map((cuenta) => (
          <option key={cuenta.id} value={cuenta.id}>
            {cuenta.codigo} {cuenta.nombre}
          </option>
        ))}
      </Selector>
    </>
  );
}

function CorregirConcepto({
  concepto,
  cuentas,
  onCerrar,
}: {
  readonly concepto: ConceptoDeCaja;
  readonly cuentas: readonly CuentaParaLaCaja[];
  readonly onCerrar: () => void;
}): React.ReactElement {
  const { t } = useTranslation();
  const clienteDeConsultas = useQueryClient();
  const textoDeError = useTextoDeError();

  const [nombre, setNombre] = useState(concepto.nombre);
  const [sentido, setSentido] = useState<Sentido>(concepto.sentido);
  // Una cuenta que ya no se admite no se ofrece: hay que elegir otra para poder guardar.
  const [cuentaId, setCuentaId] = useState(
    concepto.cuenta === null ? '' : concepto.cuentaContableId,
  );
  const [activo, setActivo] = useState(concepto.activo);
  const [error, setError] = useState<string | null>(null);

  const guardar = useMutation({
    mutationFn: () =>
      pedir<unknown>(`/conceptos-de-caja/${concepto.id}`, {
        method: 'PATCH',
        cuerpo: {
          nombre: nombre.trim(),
          sentido,
          ...(cuentaId === '' ? {} : { cuentaContableId: cuentaId }),
          activo,
        },
      }),
    onSuccess: async () => {
      await clienteDeConsultas.invalidateQueries({ queryKey: ['conceptos-de-caja'] });
      onCerrar();
    },
    onError: (fallo: unknown) => {
      setError(textoDeError(fallo));
    },
  });

  return (
    <Dialogo
      abierto
      titulo={t('conceptosDeCaja.corregir')}
      descripcion={t('conceptosDeCaja.ayudaCorregir')}
      onCerrar={onCerrar}
      acciones={
        <Boton
          tono="principal"
          cargando={guardar.isPending}
          disabled={nombre.trim() === '' || (cuentaId === '' && activo)}
          onClick={() => {
            guardar.mutate();
          }}
        >
          {t('acciones.guardar')}
        </Boton>
      }
    >
      <div className="flex flex-col gap-3">
        <CamposDelConcepto
          cuentas={cuentas}
          nombre={nombre}
          sentido={sentido}
          cuentaId={cuentaId}
          onNombre={setNombre}
          onSentido={setSentido}
          onCuenta={setCuentaId}
        />
        <Interruptor
          etiqueta={t('conceptosDeCaja.activo')}
          checked={activo}
          onChange={(evento) => {
            setActivo(evento.target.checked);
          }}
        />
        {error === null ? null : (
          <p role="alert" className="text-xs text-mal">
            {error}
          </p>
        )}
      </div>
    </Dialogo>
  );
}
